import React, { useState, useEffect } from "react";
import { Link } from "@/i18n/routing";
import { Navigation } from "../layout/Navigation";
import { Button } from "../ui/button";
import { PhoneIcon, Menu, X, LogIn } from "lucide-react";
import { useTranslations, useLocale } from "next-intl";
import { LanguageSwitcher } from "../ui/language-switcher";
import { ProfileDropdown } from "../ui/profile-dropdown";
import { useNavigation, type NavigationItem, type NavigationChild } from "@/hooks/useNavigation";
import { useAuth } from "@/hooks/useAuth";
import { areChildLinksDisabled, isFeatureEnabled, NavigationFeatureKey } from "@/config/navigationFeatures";

export const NavBarSection: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const t = useTranslations('Navigation');
  const tMega = useTranslations('megaMenu');
  const locale = useLocale();
  const navigationItems = useNavigation();
  const { user, loading, logout } = useAuth();


  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);
      setIsScrolled(currentScrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuOpen) {
        const mobileMenuElement = document.querySelector('[data-mobile-menu]');
        const mobileMenuButton = document.querySelector('[data-mobile-menu-button]');
        
        if (mobileMenuElement && !mobileMenuElement.contains(event.target as Node) &&
            mobileMenuButton && !mobileMenuButton.contains(event.target as Node)) {
          setMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : 'unset';

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => {
    // Add closing animation
    const menuElement = document.querySelector('[data-mobile-menu]');
    const overlayElement = document.querySelector('[data-mobile-overlay]');
    
    if (menuElement) {
      menuElement.classList.remove('animate-slideInRight');
      menuElement.classList.add('animate-slideOutRight');
    }
    
    if (overlayElement) {
      overlayElement.classList.remove('animate-fadeIn');
      overlayElement.classList.add('animate-fadeOut');
    }
    
    // Delay unmount to allow exit animation
    setTimeout(() => {
      setMobileMenuOpen(false);
      setActiveDropdown(null);
    }, 300);
  };

  const handleMobileDropdownToggle = (itemId: string) => {
    setActiveDropdown(activeDropdown === itemId ? null : itemId);
  };

  // Check if item should use mega menu
  const shouldUseMegaMenu = (itemId: string) => {
    const allowed: NavigationFeatureKey[] = ['solutions', 'industries', 'resources', 'company', 'services'];
    const key = itemId as NavigationFeatureKey;
    return allowed.includes(key) && isFeatureEnabled(key);
  };

  // Get mega menu items for mobile
  const getMegaMenuItems = (menuKey: string) => {
    try {
      const sections = [];
      const sectionCount = tMega.raw(`${menuKey}.sectionsCount`) as number || 1;
      const disableLinks = areChildLinksDisabled(menuKey as NavigationFeatureKey);
      const baseChildren = navigationItems.find((navItem) => navItem.id === menuKey)?.children ?? [];
      let baseIndex = 0;
      const takeBaseChild = () => {
        const child = baseChildren[baseIndex] ?? null;
        baseIndex += 1;
        return child;
      };
      
      for (let i = 0; i < sectionCount; i++) {
        const sectionKey = `section${i + 1}`;
        const itemsCount = tMega.raw(`${menuKey}.${sectionKey}.itemsCount`) as number || 0;
        const items = [];
        
        for (let j = 0; j < itemsCount; j++) {
          const itemKey = `${sectionKey}.item${j + 1}`;
          const translationLabel = tMega(`${menuKey}.${itemKey}.label`);
          const description = tMega(`${menuKey}.${itemKey}.description`);
          const baseChild = takeBaseChild();
          const label = baseChild?.label ?? translationLabel;
          const href = disableLinks ? undefined : baseChild?.href;
          
          if (label) {
            items.push({
              id: `${sectionKey}-item-${j + 1}`,
              label,
              href,
              description: description || ''
            });
          }
        }
        
        if (items.length > 0) {
          sections.push({
            id: sectionKey,
            title: tMega(`${menuKey}.${sectionKey}.title`),
            items: items
          });
        }
      }
      
      return sections;
    } catch (error) {
      return [];
    }
  };

  return (
    <nav className="main-navbar">
      {/* Fixed Navbar - Only visible when scrolled */}
      {isScrolled && (
        <section 
          className="fixed top-0 left-0 right-0 w-full z-50 bg-[#1c2042]/95 backdrop-blur-md shadow-2xl transition-all duration-500 ease-out overflow-visible"
          style={{
            backdropFilter: 'blur(12px) saturate(180%)',
          }}
        >
          {/* Animated Background Gradient */}
          <div 
            className="absolute inset-0 opacity-100 transition-opacity duration-700"
            style={{
              background: 'linear-gradient(135deg, rgba(28, 32, 66, 0.95) 0%, rgba(26, 26, 46, 0.95) 50%, rgba(22, 33, 62, 0.95) 100%)',
            }}
          />
          
          {/* Floating Particles Effect */}
          <div className="absolute inset-0 pointer-events-none opacity-30 transition-opacity duration-700">
            <div className="absolute top-2 left-1/4 w-1 h-1 bg-brand-secondary rounded-full animate-ping opacity-60" style={{ animationDelay: '0.1s' }}></div>
            <div className="absolute top-4 right-1/3 w-0.5 h-0.5 bg-brand-primary rounded-full animate-ping opacity-60" style={{ animationDelay: '0.3s' }}></div>
            <div className="absolute bottom-2 left-1/2 w-1 h-1 bg-white rounded-full animate-ping opacity-60" style={{ animationDelay: '0.5s' }}></div>
            <div className="absolute bottom-4 right-1/4 w-0.5 h-0.5 bg-brand-secondary rounded-full animate-ping opacity-60" style={{ animationDelay: '0.7s' }}></div>
          </div>

          <div className="container mx-auto px-4 lg:px-6 xl:px-8 relative z-10 overflow-visible">
            <div className="flex items-center justify-between h-[70px] transition-all duration-500">
              {/* Left Section: Logo + Desktop Navigation */}
              <div className="flex items-center gap-8 lg:gap-12">
                {/* Logo */}
                <Link href={{ pathname: '/' }} locale={locale} className="flex-shrink-0 cursor-pointer">
                  <div className="relative group">
                    {/* Logo Glow Effect */}
                    <div className="absolute -inset-2 rounded-xl bg-gradient-to-r from-brand-primary/20 to-brand-secondary/20 blur-sm opacity-60 transition-all duration-500" />
                    
                    <img
                      className="w-[65px] h-auto md:w-[75px] lg:w-[85px] object-contain transition-all duration-500 group-hover:scale-105"
                      alt="Company Logo"
                      src="/svg/logo.svg"
                    />
                    
                    {/* Hover Shimmer Effect */}
                    <div className="absolute inset-0 -top-1 -left-1 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                  </div>
                </Link>
                
                {/* Desktop Navigation */}
                <div className="hidden min-[1280px]:block">
                  <Navigation />
                </div>
              </div>

              {/* Right Section: Language Switcher + Contact Us Button + Mobile Menu */}
              <div className="flex items-center gap-6">
                {/* Language Switcher */}
                <div className="hidden min-[1280px]:block translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:400ms]">
                  <LanguageSwitcher />
                </div>
                
                {/* Enhanced Contact Us Button */}
                <div className="hidden min-[370px]:block translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:500ms]">
                  <div className="relative group cursor-pointer">
                    {/* Content with Enhanced Interactions */}
                    <Link href="/contact" className="flex items-center justify-center gap-2 relative z-10 group-hover:scale-105 transition-all duration-300 rtl:flex-row-reverse">
                      {/* Enhanced Phone Icon with Magnetic Effect */}
                      <div className="relative group/icon">
                        <div className="absolute inset-0 bg-brand-gradient rounded-full blur-sm opacity-0 group-hover/icon:opacity-40 transition-all duration-500 scale-0 group-hover/icon:scale-110"></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-brand-secondary to-brand-primary rounded-full blur-xs opacity-0 group-hover/icon:opacity-30 transition-all duration-300 scale-0 group-hover/icon:scale-125"></div>
                        <PhoneIcon className="w-4 h-4 text-white relative z-10 transition-all duration-300 group-hover/icon:scale-110 group-hover/icon:rotate-12" />
                      </div>
                      
                      {/* Enhanced Text with Glow Effect */}
                      <span className="font-button font-[number:var(--button-font-weight)] text-white text-sm md:text-[length:var(--button-font-size)] tracking-[var(--button-letter-spacing)] leading-[var(--button-line-height)] [font-style:var(--button-font-style)] transition-all duration-300 group-hover:text-white/90 group-hover:drop-shadow-sm">
                        {t('contactUs')}
                      </span>
                    </Link>
                  </div>
                </div>

                {/* Login/Profile Button */}
                <div className="hidden min-[360px]:block translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:600ms]">
                  {loading ? (
                    <div className="w-24 h-10 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] rounded-2xl animate-pulse"></div>
                  ) : user ? (
                    <ProfileDropdown user={user} onLogout={logout} />
                  ) : (
                    <div className="relative group cursor-pointer">
                      {/* Enhanced Outer Glow Ring with Animation */}
                      <div className="absolute -inset-3 rounded-2xl bg-gradient-to-r from-brand-primary via-brand-secondary to-brand-primary opacity-0 group-hover:opacity-30 blur-lg animate-pulse transition-all duration-700 ease-out" />
                      
                      {/* Secondary Glow Ring */}
                      <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-brand-secondary via-brand-primary to-brand-secondary opacity-0 group-hover:opacity-20 blur-md transition-all duration-500 ease-out" />
                      
                      {/* Main Button Container with Enhanced Effects */}
                      <div className="relative overflow-hidden bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] rounded-2xl p-[2px] shadow-lg transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-brand-secondary/25">
                        
                        {/* Animated Background Gradient */}
                        <div className="absolute inset-0 bg-gradient-to-r from-brand-secondary/10 via-brand-primary/10 to-brand-secondary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
                        
                        {/* Shimmer Effect */}
                        <div className="absolute inset-0 -top-1 -left-1 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 opacity-0 group-hover:opacity-100 group-hover:animate-shimmer transition-all duration-500 rounded-2xl" />
                        
                        {/* Inner Button with Enhanced Styling */}
                        <div className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] rounded-2xl px-4 py-2 transition-all duration-500 group-hover:from-[#1f1f3a] group-hover:via-[#1a2a4a] group-hover:to-[#0f4a70]">
                          
                          {/* Floating Particles Effect */}
                          <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                            <div className="absolute top-1 left-2 w-1 h-1 bg-brand-primary rounded-full animate-ping opacity-60" style={{ animationDelay: '0.1s' }}></div>
                            <div className="absolute top-2 right-3 w-0.5 h-0.5 bg-brand-secondary rounded-full animate-ping opacity-60" style={{ animationDelay: '0.3s' }}></div>
                            <div className="absolute bottom-1 left-3 w-0.5 h-0.5 bg-white rounded-full animate-ping opacity-60" style={{ animationDelay: '0.5s' }}></div>
                            <div className="absolute bottom-2 right-2 w-1 h-1 bg-brand-primary rounded-full animate-ping opacity-60" style={{ animationDelay: '0.7s' }}></div>
                          </div>
                          
                          {/* Content with Enhanced Interactions */}
                          <Link href="/login" className="flex items-center justify-center gap-2 relative z-10 group-hover:scale-105 transition-all duration-300 rtl:flex-row-reverse">
                            {/* Enhanced Login Icon with Magnetic Effect */}
                            <div className="relative group/icon">
                              <div className="absolute inset-0 bg-brand-gradient rounded-full blur-sm opacity-0 group-hover/icon:opacity-40 transition-all duration-500 scale-0 group-hover/icon:scale-110"></div>
                              <div className="absolute inset-0 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-full blur-xs opacity-0 group-hover/icon:opacity-30 transition-all duration-300 scale-0 group-hover/icon:scale-125"></div>
                              <LogIn className="w-4 h-4 text-white relative z-10 transition-all duration-300 group-hover/icon:scale-110 group-hover/icon:-rotate-12" />
                            </div>
                            
                            {/* Enhanced Text with Glow Effect */}
                            <span className="font-button font-[number:var(--button-font-weight)] text-white text-sm md:text-[length:var(--button-font-size)] tracking-[var(--button-letter-spacing)] leading-[var(--button-line-height)] [font-style:var(--button-font-style)] transition-all duration-300 group-hover:text-white/90 group-hover:drop-shadow-sm">
                              Login
                            </span>
                          </Link>
                          
                          {/* Ripple Effect Container */}
                          <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
                            <div className="absolute inset-0 bg-gradient-to-r from-brand-secondary/20 to-brand-primary/20 scale-0 group-active:scale-100 group-active:opacity-0 transition-all duration-300 rounded-full"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Mobile Menu Button */}
                <button 
                  className="min-[1280px]:hidden relative z-[9999] p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900 transition-colors duration-200 hover:text-blue-200 hover:bg-white/10 rounded-lg"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  data-mobile-menu-button
                >
                  {mobileMenuOpen ? (
                    <X className="w-6 h-6" />
                  ) : (
                    <Menu className="w-6 h-6" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Original Navbar - Always present */}
      <section className="w-full bg-[#1c2042] relative z-40">
        <div className="container mx-auto px-4 lg:px-6 xl:px-8">
          <div className="h-[85px] flex items-center justify-between">
            
            {/* Mobile Layout: Same as Sticky Navbar */}
            <div className="flex min-[1280px]:hidden items-center justify-between w-full">
              {/* Left Section: Logo */}
              <div className="flex items-center">
                {/* Logo */}
                <Link href={{ pathname: '/' }} locale={locale} className="flex-shrink-0 cursor-pointer">
                  <div className="relative group">
                    {/* Logo Glow Effect */}
                    <div className="absolute -inset-2 rounded-xl bg-gradient-to-r from-brand-primary/10 to-brand-secondary/10 blur-sm opacity-0 transition-all duration-500" />
                    
                    <img
                      className="w-[65px] h-auto object-contain transition-all duration-500 group-hover:scale-105"
                      alt="Company Logo"
                      src="/svg/logo.svg"
                    />
                    
                    {/* Hover Shimmer Effect */}
                    <div className="absolute inset-0 -top-1 -left-1 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                  </div>
                </Link>
              </div>

              {/* Right Section: Contact Us Button + Mobile Menu */}
              <div className="flex items-center gap-4">
                  {/* Enhanced Contact Us Button */}
                <div className="hidden min-[370px]:block translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:500ms]">
                  <div className="relative group cursor-pointer">
                    {/* Content with Enhanced Interactions */}
                    <Link href="/contact" className="flex items-center justify-center gap-2 relative z-10 group-hover:scale-105 transition-all duration-300 rtl:flex-row-reverse">
                      {/* Enhanced Phone Icon with Magnetic Effect */}
                      <div className="relative group/icon">
                        <div className="absolute inset-0 bg-brand-gradient rounded-full blur-sm opacity-0 group-hover/icon:opacity-40 transition-all duration-500 scale-0 group-hover/icon:scale-110"></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-brand-secondary to-brand-primary rounded-full blur-xs opacity-0 group-hover/icon:opacity-30 transition-all duration-300 scale-0 group-hover/icon:scale-125"></div>
                        <PhoneIcon className="w-4 h-4 text-white relative z-10 transition-all duration-300 group-hover/icon:scale-110 group-hover/icon:rotate-12" />
                      </div>
                      
                      {/* Enhanced Text with Glow Effect */}
                      <span className="font-button font-[number:var(--button-font-weight)] text-white text-sm md:text-[length:var(--button-font-size)] tracking-[var(--button-letter-spacing)] leading-[var(--button-line-height)] [font-style:var(--button-font-style)] transition-all duration-300 group-hover:text-white/90 group-hover:drop-shadow-sm">
                        {t('contactUs')}
                      </span>
                    </Link>
                  </div>
                </div>

                  {/* Login/Profile Button - Scrolled */}
                <div className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:600ms]">
                  {loading ? (
                    <div className="w-24 h-10 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] rounded-2xl animate-pulse"></div>
                  ) : user ? (
                    <ProfileDropdown user={user} onLogout={logout} />
                  ) : (
                    <div className="relative group cursor-pointer">
                      {/* Enhanced Outer Glow Ring with Animation */}
                      <div className="absolute -inset-3 rounded-2xl bg-gradient-to-r from-brand-primary via-brand-secondary to-brand-primary opacity-0 group-hover:opacity-30 blur-lg animate-pulse transition-all duration-700 ease-out" />
                      
                      {/* Secondary Glow Ring */}
                      <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-brand-secondary via-brand-primary to-brand-secondary opacity-0 group-hover:opacity-20 blur-md transition-all duration-500 ease-out" />
                      
                      {/* Main Button Container with Enhanced Effects */}
                      <div className="relative overflow-hidden bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] rounded-2xl p-[2px] shadow-lg transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-brand-secondary/25">
                        
                        {/* Animated Background Gradient */}
                        <div className="absolute inset-0 bg-gradient-to-r from-brand-secondary/10 via-brand-primary/10 to-brand-secondary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
                        
                        {/* Shimmer Effect */}
                        <div className="absolute inset-0 -top-1 -left-1 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 opacity-0 group-hover:opacity-100 group-hover:animate-shimmer transition-all duration-500 rounded-2xl" />
                        
                        {/* Inner Button with Enhanced Styling */}
                        <div className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] rounded-2xl px-4 py-2 transition-all duration-500 group-hover:from-[#1f1f3a] group-hover:via-[#1a2a4a] group-hover:to-[#0f4a70]">
                          
                          {/* Floating Particles Effect */}
                          <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                            <div className="absolute top-1 left-2 w-1 h-1 bg-brand-primary rounded-full animate-ping opacity-60" style={{ animationDelay: '0.1s' }}></div>
                            <div className="absolute top-2 right-3 w-0.5 h-0.5 bg-brand-secondary rounded-full animate-ping opacity-60" style={{ animationDelay: '0.3s' }}></div>
                            <div className="absolute bottom-1 left-3 w-0.5 h-0.5 bg-white rounded-full animate-ping opacity-60" style={{ animationDelay: '0.5s' }}></div>
                            <div className="absolute bottom-2 right-2 w-1 h-1 bg-brand-primary rounded-full animate-ping opacity-60" style={{ animationDelay: '0.7s' }}></div>
                          </div>
                          
                          {/* Content with Enhanced Interactions */}
                          <Link href="/login" className="flex items-center justify-center gap-2 relative z-10 group-hover:scale-105 transition-all duration-300 rtl:flex-row-reverse">
                            {/* Enhanced Login Icon with Magnetic Effect */}
                            <div className="relative group/icon">
                              <div className="absolute inset-0 bg-brand-gradient rounded-full blur-sm opacity-0 group-hover/icon:opacity-40 transition-all duration-500 scale-0 group-hover/icon:scale-110"></div>
                              <div className="absolute inset-0 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-full blur-xs opacity-0 group-hover/icon:opacity-30 transition-all duration-300 scale-0 group-hover/icon:scale-125"></div>
                              <LogIn className="w-4 h-4 text-white relative z-10 transition-all duration-300 group-hover/icon:scale-110 group-hover/icon:-rotate-12" />
                            </div>
                            
                            {/* Enhanced Text with Glow Effect */}
                            <span className="font-button font-[number:var(--button-font-weight)] text-white text-sm md:text-[length:var(--button-font-size)] tracking-[var(--button-letter-spacing)] leading-[var(--button-line-height)] [font-style:var(--button-font-style)] transition-all duration-300 group-hover:text-white/90 group-hover:drop-shadow-sm">
                              Login
                            </span>

                          </Link>
                          
                          {/* Ripple Effect Container */}
                          <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
                            <div className="absolute inset-0 bg-gradient-to-r from-brand-secondary/20 to-brand-primary/20 scale-0 group-active:scale-100 group-active:opacity-0 transition-all duration-300 rounded-full"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Mobile Menu Button */}
                <button 
                  className="relative z-[9999] p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900 transition-colors duration-200 hover:text-blue-200 hover:bg-white/10 rounded-lg"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  data-mobile-menu-button
                >
                  {mobileMenuOpen ? (
                    <X className="w-6 h-6" />
                  ) : (
                    <Menu className="w-6 h-6" />
                  )}
                </button>
              </div>
            </div>

            {/* Desktop Layout: Logo + Navigation (Left) | Contact Us Button + Mobile Menu (Right) */}
            <div className="hidden min-[1280px]:flex items-center justify-between w-full">
              {/* Left Section: Logo + Desktop Navigation */}
              <div className="flex items-center gap-8 lg:gap-12">
                {/* Logo */}
                <Link href={{ pathname: '/' }} locale={locale} className="flex-shrink-0 cursor-pointer">
                  <div className="relative group">
                    {/* Logo Glow Effect */}
                    <div className="absolute -inset-2 rounded-xl bg-gradient-to-r from-brand-primary/10 to-brand-secondary/10 blur-sm opacity-0 transition-all duration-500" />
                    
                    <img
                      className="w-[75px] h-auto md:w-[85px] lg:w-[100px] object-contain transition-all duration-500 group-hover:scale-105 translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:300ms]"
                      alt="Company Logo"
                      src="/svg/logo.svg"
                    />
                    
                    {/* Hover Shimmer Effect */}
                    <div className="absolute inset-0 -top-1 -left-1 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                  </div>
                </Link>
                
                {/* Desktop Navigation */}
                <div className="hidden min-[1280px]:block">
                  <Navigation />
                </div>
              </div>

              {/* Right Section: Language Switcher + Contact Us Button + Mobile Menu */}
              <div className="flex items-center gap-6">
                {/* Language Switcher */}
                <div className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:400ms]">
                  <LanguageSwitcher />
                </div>
                
                 {/* Enhanced Contact Us Button */}
                <div className="hidden min-[370px]:block translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:500ms]">
                  <div className="relative group cursor-pointer">
                    {/* Content with Enhanced Interactions */}
                    <Link href="/contact" className="flex items-center justify-center gap-2 relative z-10 group-hover:scale-105 transition-all duration-300 rtl:flex-row-reverse">
                      {/* Enhanced Phone Icon with Magnetic Effect */}
                      <div className="relative group/icon">
                        <div className="absolute inset-0 bg-brand-gradient rounded-full blur-sm opacity-0 group-hover/icon:opacity-40 transition-all duration-500 scale-0 group-hover/icon:scale-110"></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-brand-secondary to-brand-primary rounded-full blur-xs opacity-0 group-hover/icon:opacity-30 transition-all duration-300 scale-0 group-hover/icon:scale-125"></div>
                        <PhoneIcon className="w-4 h-4 text-white relative z-10 transition-all duration-300 group-hover/icon:scale-110 group-hover/icon:rotate-12" />
                      </div>
                      
                      {/* Enhanced Text with Glow Effect */}
                      <span className="font-button font-[number:var(--button-font-weight)] text-white text-sm md:text-[length:var(--button-font-size)] tracking-[var(--button-letter-spacing)] leading-[var(--button-line-height)] [font-style:var(--button-font-style)] transition-all duration-300 group-hover:text-white/90 group-hover:drop-shadow-sm">
                        {t('contactUs')}
                      </span>
                    </Link>
                  </div>
                </div>

              {/* Login/Profile Button - Scrolled */}
              <div className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:600ms]">
                {loading ? (
                  <div className="w-24 h-10 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] rounded-2xl animate-pulse"></div>
                ) : user ? (
                  <ProfileDropdown user={user} onLogout={logout} />
                ) : (
                  <div className="relative group cursor-pointer">
                    {/* Enhanced Outer Glow Ring with Animation */}
                    <div className="absolute -inset-3 rounded-2xl bg-gradient-to-r from-brand-primary via-brand-secondary to-brand-primary opacity-0 group-hover:opacity-30 blur-lg animate-pulse transition-all duration-700 ease-out" />
                    
                    {/* Secondary Glow Ring */}
                    <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-brand-secondary via-brand-primary to-brand-secondary opacity-0 group-hover:opacity-20 blur-md transition-all duration-500 ease-out" />
                    
                    {/* Main Button Container with Enhanced Effects */}
                    <div className="relative overflow-hidden bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] rounded-2xl p-[2px] shadow-lg transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-brand-secondary/25">
                      
                      {/* Animated Background Gradient */}
                      <div className="absolute inset-0 bg-gradient-to-r from-brand-secondary/10 via-brand-primary/10 to-brand-secondary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
                      
                      {/* Shimmer Effect */}
                      <div className="absolute inset-0 -top-1 -left-1 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 opacity-0 group-hover:opacity-100 group-hover:animate-shimmer transition-all duration-500 rounded-2xl" />
                      
                      {/* Inner Button with Enhanced Styling */}
                      <div className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] rounded-2xl px-4 py-2 transition-all duration-500 group-hover:from-[#1f1f3a] group-hover:via-[#1a2a4a] group-hover:to-[#0f4a70]">
                        
                        {/* Floating Particles Effect */}
                        <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                          <div className="absolute top-1 left-2 w-1 h-1 bg-brand-primary rounded-full animate-ping opacity-60" style={{ animationDelay: '0.1s' }}></div>
                          <div className="absolute top-2 right-3 w-0.5 h-0.5 bg-brand-secondary rounded-full animate-ping opacity-60" style={{ animationDelay: '0.3s' }}></div>
                          <div className="absolute bottom-1 left-3 w-0.5 h-0.5 bg-white rounded-full animate-ping opacity-60" style={{ animationDelay: '0.5s' }}></div>
                          <div className="absolute bottom-2 right-2 w-1 h-1 bg-brand-primary rounded-full animate-ping opacity-60" style={{ animationDelay: '0.7s' }}></div>
                        </div>
                        
                        {/* Content with Enhanced Interactions */}
                        <Link href="/login" className="flex items-center justify-center gap-2 relative z-10 group-hover:scale-105 transition-all duration-300 rtl:flex-row-reverse">
                          {/* Enhanced Login Icon with Magnetic Effect */}
                          <div className="relative group/icon">
                            <div className="absolute inset-0 bg-brand-gradient rounded-full blur-sm opacity-0 group-hover/icon:opacity-40 transition-all duration-500 scale-0 group-hover/icon:scale-110"></div>
                            <div className="absolute inset-0 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-full blur-xs opacity-0 group-hover/icon:opacity-30 transition-all duration-300 scale-0 group-hover/icon:scale-125"></div>
                            <LogIn className="w-4 h-4 text-white relative z-10 transition-all duration-300 group-hover/icon:scale-110 group-hover/icon:-rotate-12" />
                          </div>
                          
                          {/* Enhanced Text with Glow Effect */}
                          <span className="font-button font-[number:var(--button-font-weight)] text-white text-sm md:text-[length:var(--button-font-size)] tracking-[var(--button-letter-spacing)] leading-[var(--button-line-height)] [font-style:var(--button-font-style)] transition-all duration-300 group-hover:text-white/90 group-hover:drop-shadow-sm">
                            Login
                          </span>

                        </Link>
                        
                        {/* Ripple Effect Container */}
                        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
                          <div className="absolute inset-0 bg-gradient-to-r from-brand-secondary/20 to-brand-primary/20 scale-0 group-active:scale-100 group-active:opacity-0 transition-all duration-300 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button 
                className="min-[1280px]:hidden relative z-[9999] p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900 transition-colors duration-200 hover:text-blue-200 hover:bg-white/10 rounded-lg"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-mobile-menu-button
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Unified Mobile Navigation Overlay */}
      {mobileMenuOpen && (
        <div 
          data-mobile-overlay
          className="min-[1280px]:hidden fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm animate-fadeIn"
          onClick={closeMobileMenu}
        >
          
          {/* Mobile Menu Panel with Glassmorphism */}
          <div 
            data-mobile-menu
            className="fixed top-0 right-0 rtl:right-auto rtl:left-0 w-80 max-w-[90vw] h-full mega-menu-glass-enhanced shadow-2xl overflow-hidden z-[9999] animate-slideInRight"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Glass overlay effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/5 pointer-events-none"></div>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
            
            <div className="relative flex flex-col h-full z-0">
              {/* Mobile Menu Header */}
              <div 
                className="relative flex items-center justify-between p-6 border-b border-white/20 backdrop-blur-sm bg-white/5 z-10"
                style={{
                  animation: 'fadeInDown 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards'
                }}
              >
                <h2 className="text-xl font-semibold text-white">{t('menu')}</h2>
                <div className="relative z-[100]">
                  <LanguageSwitcher />
                </div>
              </div>

              {/* Mobile Navigation Items */}
              <div className="flex-1 overflow-y-auto p-6 relative z-0">
                <nav className="space-y-4">
                  {navigationItems.map((item: NavigationItem, index: number) => (
                    <div 
                      key={item.id} 
                      className="relative z-0"
                      style={{
                        animation: `slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.05}s both`
                      }}
                    >
                      {item.hasDropdown && item.children ? (
                        shouldUseMegaMenu(item.id) ? (
                          <div>
                            <button
                              onClick={() => handleMobileDropdownToggle(item.id)}
                              className="flex items-center justify-between w-full p-3 text-left rtl:text-right text-white hover:bg-slate-800 rounded-lg transition-all duration-300 active:scale-[0.98] relative z-0"
                            >
                              <span className="font-medium">{item.label}</span>
                              <div className={`transform transition-all duration-300 ease-out relative z-0 ${activeDropdown === item.id ? 'rotate-180' : ''}`}>
                                <svg className="w-5 h-5 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </button>
                            
                            {activeDropdown === item.id && (
                              <div 
                                className="ml-4 rtl:ml-0 rtl:mr-4 mt-2 space-y-2 overflow-hidden"
                                style={{
                                  animation: 'expandDown 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards'
                                }}
                              >
                                {getMegaMenuItems(item.id).map((section: any) => (
                                  <div key={section.id} className="space-y-2">
                                    {section.items.map((menuItem: any, itemIndex: number) => {
                                      const sharedContent = (
                                        <>
                                          <div className="font-medium mb-1">{menuItem.label}</div>
                                          {menuItem.description && (
                                            <p className="text-xs text-slate-400 mt-1 leading-relaxed">{menuItem.description}</p>
                                          )}
                                        </>
                                      );

                                      const sharedClasses = "block p-3 rounded-lg transition-all duration-200 " +
                                        (menuItem.href ? "text-slate-300 hover:text-white hover:bg-slate-800 hover:translate-x-1 rtl:hover:-translate-x-1 cursor-pointer" : "text-slate-400 bg-white/5 cursor-default opacity-90");

                                      if (menuItem.href) {
                                        return (
                                          <Link
                                            key={menuItem.id}
                                            href={menuItem.href}
                                            className={sharedClasses}
                                            style={{
                                              animation: `fadeSlideIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) ${itemIndex * 0.04}s both`
                                            }}
                                            onClick={(e) => {
                                              if (typeof window !== 'undefined') {
                                                window.dispatchEvent(new CustomEvent('navigation-start', {
                                                  detail: { href: menuItem.href }
                                                }));
                                              }
                                              closeMobileMenu();
                                            }}
                                          >
                                            {sharedContent}
                                          </Link>
                                        );
                                      }

                                      return (
                                        <div
                                          key={menuItem.id}
                                          className={sharedClasses}
                                          style={{
                                            animation: `fadeSlideIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) ${itemIndex * 0.04}s both`
                                          }}
                                        >
                                          {sharedContent}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <button
                              onClick={() => handleMobileDropdownToggle(item.id)}
                              className="flex items-center justify-between w-full p-3 text-left rtl:text-right text-white hover:bg-slate-800 rounded-lg transition-all duration-300 active:scale-[0.98] relative z-0"
                            >
                              <span className="font-medium">{item.label}</span>
                              <div className={`transform transition-all duration-300 ease-out relative z-0 ${activeDropdown === item.id ? 'rotate-180' : ''}`}>
                                <svg className="w-5 h-5 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </button>
                            
                            {activeDropdown === item.id && (
                              <div 
                                className="ml-4 rtl:ml-0 rtl:mr-4 mt-2 space-y-2 overflow-hidden"
                                style={{
                                  animation: 'expandDown 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards'
                                }}
                              >
                                {item.children.map((child: NavigationChild, childIndex: number) => (
                                  <Link
                                    key={child.id}
                                    href={child.href}
                                    className="block p-3 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-all duration-200 hover:translate-x-1 rtl:hover:-translate-x-1"
                                    style={{
                                      animation: `fadeSlideIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) ${childIndex * 0.04}s both`
                                    }}
                                    onClick={(e) => {
                                      if (typeof window !== 'undefined') {
                                        window.dispatchEvent(new CustomEvent('navigation-start', {
                                          detail: { href: child.href }
                                        }));
                                      }
                                      closeMobileMenu();
                                    }}
                                  >
                                    <div className="font-medium mb-1">{child.label}</div>
                                    {(child as any).description && (
                                      <p className="text-xs text-slate-400 mt-1 leading-relaxed">{(child as any).description}</p>
                                    )}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        )
                      ) : (
                        <Link
                          href={item.href}
                          className="block p-3 text-white hover:bg-slate-800 rounded-lg transition-colors duration-200"
                          onClick={(e) => {
                            if (typeof window !== 'undefined') {
                              window.dispatchEvent(new CustomEvent('navigation-start', {
                                detail: { href: item.href }
                              }));
                            }
                            closeMobileMenu();
                          }}
                        >
                          {item.label}
                        </Link>
                      )}
                    </div>
                  ))}
                </nav>
              </div>

              {/* Mobile Menu Footer */}
              <div className="p-6 border-t border-white/20 backdrop-blur-sm bg-white/5">
                <Button 
                  asChild
                  className="w-full bg-brand-gradient hover:opacity-90 transition-all duration-300"
                >
                  <Link href={`/${locale}/contact`} onClick={closeMobileMenu}>
                    <PhoneIcon className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
                    {t('contactUs')}
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
